<template>
  <div>
    <serveur-info :erreur="error" />
    <form @submit.prevent="validateBeforeSubmit">
      <my-notification :icon="ICON">
        <template #title>
          {{ $t('compAdminEntityLaboratory.title-1') }}
        </template>
        {{ $t('compAdminEntityLaboratory.info-1') }}
        <br />
        {{ $t('compAdminEntityLaboratory.info-2') }}
      </my-notification>
      <br />
      <entity-form v-model="laboratory" @input="inputChanged" />
      <div class="columns">
        <div class="column">
          <b-notification :closable="false">
            <div class="columns">
              <div class="column is-7">
                <b-field
                  :label="$t('compAdminEntityLaboratory.title-2') + ' *'"
                />
              </div>
              <div class="column is-2">
                <b-field
                  :label="$t('compAdminEntityLaboratory.label-2') + ' *'"
                />
              </div>
              <div class="column is-3"></div>
            </div>
            <div
              class="columns"
              v-for="(discipline, index) in laboratory.disciplines"
              :key="index"
            >
              <div class="column is-7">
                <discipline-input
                  @onDisciplineChange="disciplineChanged"
                  v-model="laboratory.disciplines[index]"
                />
              </div>
              <div class="column is-3">
                <b-input
                  name="percentage"
                  @input="inputChanged"
                  v-model="discipline.percentage"
                  v-validate="'required|numeric'"
                  icon="percent"
                ></b-input>
              </div>
              <div class="column is-1">
                <b-button icon-left="minus" @click="removeDiscipline(index)">
                </b-button>
              </div>
            </div>
            <div class="columns">
              <div class="column is-7">
                <discipline-input
                  @onDisciplineChange="disciplineChanged"
                  v-model="cdiscipline"
                />
              </div>
              <div class="column is-3">
                <b-input
                  name="percentage"
                  @input="inputChanged"
                  v-model="cdiscipline.percentage"
                  v-validate="'required|numeric'"
                  icon="percent"
                />
              </div>
              <div class="column is-1">
                <b-button
                  v-show="totalDiscPerc < 100"
                  icon-left="plus"
                  @click="addDiscipline"
                />
              </div>
            </div>
            <p class="help is-danger" v-if="totalDiscError">
              {{ $t('compAdminEntityLaboratory.error-1') }}
            </p>
            <p class="help is-danger" v-if="discError">
              {{ discError }}
            </p>
          </b-notification>
        </div>
        <div class="column">
          <b-notification :closable="false">
            <b-field
              :label="$t('compAdminEntityLaboratory.label-1') + ' *'"
              :type="{ 'is-danger': errors.has('administrations') }"
              :message="errors.first('administrations')"
            >
              <administration-input
                @input="inputChanged"
                v-model="laboratory.administrations"
              />
            </b-field>
          </b-notification>
        </div>
      </div>
      <br />
      <nav class="level">
        <div class="level-left"></div>
        <div class="buttons level-right">
          <b-button
            class="button"
            @click="initLaboratory"
            :disabled="resetDisabled"
            icon-left="close"
          >
            {{ $t('cancel') }}
          </b-button>
          <button
            type="submit"
            :disabled="saveDisabled"
            class="level-item button is-primary"
          >
            <span class="icon is-small"><i class="fa fa-save"></i></span>
            <span>{{ $t('save') }}</span>
          </button>
        </div>
      </nav>
    </form>
  </div>
</template>

<script>
import '@/plugins/leaflet.js'
import { useAdminStore } from '@/stores/admin.js'
import { Laboratory } from '@/models/core'

import _ from 'lodash'

export default {
  name: 'Laboratory',
  components: {
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    'entity-form': () => import('@/components/admin/entity/EntityForm.vue'),
    'my-notification': () => import('@/components/MyNotification.vue'),
    'administration-input': () =>
      import('@/components/core/AdministrationInput.vue'),
    'discipline-input': () => import('@/components/core/DisciplineInput.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    loaded: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      autoIsSelected: false,
      isFetching: false,
      discError: '',
      isDiscError: true,
      totalDiscError: false,
      cdiscipline: {
        id: null,
        percentage: 100,
        name: ''
      },
      error: '',
      hasChanged: false,
      laboratory: Laboratory.createEmpty()
    }
  },
  setup () {
    const adminStore = useAdminStore()
    return { adminStore }
  },
  computed: {
    savedLaboratory: function () {
      return this.adminStore.entity
    },
    ICON: function () {
      return Laboratory.ICON
    },
    totalDiscPerc: function () {
      let percTotal = 0
      for (let disc of this.laboratory.disciplines) {
        percTotal += parseInt(disc.percentage)
      }
      if (!isNaN(parseInt(this.cdiscipline.percentage))) {
        percTotal += parseInt(this.cdiscipline.percentage)
      }
      return percTotal
    },
    resetDisabled: function () {
      let toReturn = true
      if (this.laboratory.id !== null) {
        toReturn = !this.hasChanged
      } else {
        toReturn = true
      }
      return toReturn
    },
    saveDisabled: function () {
      let toReturn = true
      if (!this.cdiscipline) {
        return false
      }
      if (this.laboratory.id !== null) {
        if (
          !this.totalDiscError &&
          !this.isDiscError &&
          this.laboratory.administrations.length > 0
        ) {
          toReturn = !this.hasChanged
        } else {
          toReturn = true
        }
      } else {
        toReturn = !(
          this.laboratory.name &&
          this.laboratory.mainSite &&
          this.laboratory.administrations.length > 0 &&
          !this.isDiscError &&
          !this.totalDiscError &&
          this.laboratory.citySize
        )
      }
      return toReturn
    }
  },
  methods: {
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const loadingComponent = this.$buefy.loading.open({
            container: null
          })
          this.laboratory.disciplines.push({
            id: this.cdiscipline.id,
            name: this.cdiscipline.name,
            percentage: parseInt(this.cdiscipline.percentage)
          })
          this.adminStore
            .saveEntity(Laboratory.createFromObj(this.laboratory))
            .then((data) => {
              this.initLaboratory()
              loadingComponent.close()
              window.scrollTo(0, 0)
              this.$buefy.toast.open({
                type: 'is-light',
                message: this.$t('compAdminEntityLaboratory.save-msg')
              })
            })
            .catch((error) => {
              this.initLaboratory()
              loadingComponent.close()
              this.error = error.response.data
            })
        }
      })
    },
    initLaboratory () {
      this.laboratory = _.cloneDeep(this.savedLaboratory)
      this.hasChanged = false
      this.error = ''
      let ld = this.laboratory.disciplines.pop()
      if (ld) {
        this.cdiscipline.id = ld.id
        this.cdiscipline.name = ld.name
        this.cdiscipline.percentage = ld.percentage
      }
    },
    addDiscipline () {
      if (this.totalDiscPerc <= 100) {
        this.laboratory.disciplines.push({
          id: this.cdiscipline.id,
          name: this.cdiscipline.name,
          percentage: parseInt(this.cdiscipline.percentage)
        })
        this.cdiscipline.id = null
        this.cdiscipline.name = ''
        this.cdiscipline.percentage =
          100 - this.totalDiscPerc + parseInt(this.cdiscipline.percentage)
        this.inputChanged()
      }
    },
    removeDiscipline (index) {
      this.laboratory.disciplines.splice(index, 1)
      this.cdiscipline.percentage =
        100 - this.totalDiscPerc + parseInt(this.cdiscipline.percentage)
      this.inputChanged()
    },
    inputChanged () {
      this.hasChanged = true
    },
    disciplineChanged (discipline) {
      this.discError = ''
      this.isDiscError = false
      for (let disc of this.laboratory.disciplines) {
        if (disc.id === null) {
          this.discError =
            this.$t('compAdminEntityLaboratory.error-2') +
            ' « ' +
            disc.name +
            ' » ' +
            this.$t('compAdminEntityLaboratory.error-3') +
            '.'
          this.isDiscError = true
        }
      }
      if (this.cdiscipline.id === null) {
        this.discError =
          this.$t('compAdminEntityLaboratory.error-2') +
          ' « ' +
          this.cdiscipline.name +
          ' » ' +
          this.$t('compAdminEntityLaboratory.error-3') +
          '.'
        this.isDiscError = true
      }
      this.inputChanged()
    }
  },
  watch: {
    loaded: function () {
      this.initLaboratory()
    },
    totalDiscPerc: function () {
      if (this.totalDiscPerc !== 100) {
        this.totalDiscError = true
      } else {
        this.totalDiscError = false
      }
    },
    '$i18n.locale': function () {
      this.disciplineChanged()
    }
  },
  mounted () {
    this.initLaboratory()
  }
}
</script>
